/*
 * ESP brand page (sections/esp-brand-page.liquid, 1 Oct 2026).
 * - "Read more" on the description.
 * - The model search: filters the cards as you type, hides series with no match, keeps the series bar's counts in
 *   step, and says how many match.
 * - The series links: a chip scrolls to its series and moves focus to its heading. On phones they are one row that
 *   swipes (arrows and click-and-drag for a mouse). The header (it hides as you scroll down) sets --esp-brand-top,
 *   which keeps the series titles clear of it.
 * Written for older iPhones too (iOS 13+): no optional chaining or replaceChildren.
 */
(() => {
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class EspBrandPage extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.groups = Array.from(this.querySelectorAll('[data-series]'));
      this.cards = Array.from(this.querySelectorAll('.esp-brand__model'));
      this.setupMore();
      this.setupSearch();
      this.setupBar();
    }

    setupMore() {
      const desc = this.querySelector('[data-desc]');
      const more = this.querySelector('[data-more]');
      if (!desc || !more) return;
      desc.setAttribute('data-clamped', '');
      if (desc.scrollHeight - desc.clientHeight < 4) {
        desc.removeAttribute('data-clamped');
        return;
      }
      more.hidden = false;
      more.addEventListener('click', () => {
        const open = more.getAttribute('aria-expanded') === 'true';
        more.setAttribute('aria-expanded', String(!open));
        more.textContent = open ? 'Read more' : 'Read less';
        if (open) desc.setAttribute('data-clamped', '');
        else desc.removeAttribute('data-clamped');
      });
    }

    setupSearch() {
      this.input = this.querySelector('[data-search]');
      if (!this.input) return;
      this.status = this.querySelector('[data-status]');
      this.empty = this.querySelector('[data-empty]');
      let timer = 0;
      this.input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.filter(), 120);
      });
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.input.value) {
          this.input.value = '';
          this.filter();
        }
      });
      const clear = this.querySelector('[data-clear]');
      if (clear) {
        clear.addEventListener('click', () => {
          this.input.value = '';
          this.filter();
          this.input.focus();
        });
      }
    }

    filter() {
      const query = this.input.value.trim();
      const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
      let shown = 0;
      this.cards.forEach((card) => {
        const text = card.getAttribute('data-search') || '';
        const match = terms.every((term) => text.indexOf(term) >= 0);
        card.hidden = !match;
        if (match) shown += 1;
      });
      this.groups.forEach((group) => {
        const visible = group.querySelectorAll('.esp-brand__model:not([hidden])').length;
        group.hidden = !visible;
        const count = group.querySelector('[data-count]');
        if (count) count.textContent = String(visible);
        const chip = this.querySelector('[data-chip="' + group.id + '"]');
        if (chip) {
          chip.parentElement.hidden = !visible;
          const chipCount = chip.querySelector('[data-chip-count]');
          if (chipCount) chipCount.textContent = String(visible);
        }
      });
      if (this.empty) {
        this.empty.hidden = shown > 0;
        const echo = this.empty.querySelector('[data-empty-query]');
        if (echo) echo.textContent = query;
      }
      if (this.status) {
        this.status.textContent = terms.length ? shown + ' of ' + this.cards.length + ' models match' : this.cards.length + ' models';
      }
    }

    setupBar() {
      this.bar = this.querySelector('[data-bar]');
      if (!this.bar) return;
      this.list = this.bar.querySelector('[data-chips]');
      const stickyHeader = document.querySelector('sticky-header');
      this.header = (stickyHeader && stickyHeader.closest('header')) || document.querySelector('header');
      this.top = -1;
      const place = () => {
        this.frame = 0;
        let top = 0;
        if (this.header) top = Math.max(0, Math.round(this.header.getBoundingClientRect().bottom));
        if (top !== this.top) {
          this.top = top;
          this.style.setProperty('--esp-brand-top', top + 'px');
        }
      };
      const queue = () => {
        if (!this.frame) this.frame = requestAnimationFrame(place);
      };
      window.addEventListener('scroll', queue, {passive: true});
      window.addEventListener('resize', queue);
      place();

      this.bar.addEventListener('click', (event) => {
        const chip = event.target.closest('[data-chip]');
        if (!chip) return;
        const target = document.getElementById(chip.getAttribute('data-chip'));
        if (!target) return;
        event.preventDefault();
        const y = target.getBoundingClientRect().top + window.pageYOffset - Math.max(0, this.top) - 16;
        window.scrollTo({top: Math.max(0, y), behavior: reducedMotion() ? 'auto' : 'smooth'});
        const heading = target.querySelector('h3');
        if (heading) heading.focus({preventScroll: true});
        if (window.history && window.history.replaceState) window.history.replaceState(null, '', '#' + target.id);
      });

      this.setupScroller();
    }

    // The chip list scrolls sideways: arrows and faded edges where there is more, and click-and-drag with a mouse.
    setupScroller() {
      const list = this.list;
      const inner = this.querySelector('[data-bar-inner]');
      if (!list || !inner) return;
      const prev = inner.querySelector('[data-scroll="-1"]');
      const next = inner.querySelector('[data-scroll="1"]');
      const update = () => {
        const max = list.scrollWidth - list.clientWidth;
        const left = list.scrollLeft > 2;
        const right = list.scrollLeft < max - 2;
        if (prev) prev.hidden = !left;
        if (next) next.hidden = !right;
        if (left) inner.setAttribute('data-more-left', '');
        else inner.removeAttribute('data-more-left');
        if (right) inner.setAttribute('data-more-right', '');
        else inner.removeAttribute('data-more-right');
      };
      const scrollList = (left) => {
        if (list.scrollTo) list.scrollTo({left: left, behavior: reducedMotion() ? 'auto' : 'smooth'});
        else list.scrollLeft = left;
      };
      [prev, next].forEach((button) => {
        if (!button) return;
        button.addEventListener('click', () => {
          const step = Number(button.getAttribute('data-scroll')) * Math.max(160, list.clientWidth * 0.7);
          scrollList(list.scrollLeft + step);
        });
      });
      list.addEventListener('scroll', update, {passive: true});
      window.addEventListener('resize', update);
      update();

      // Click-and-drag with a mouse; a drag doesn't count as a click on the chip it started on.
      let startX = 0;
      let startLeft = 0;
      let dragging = false;
      let moved = false;
      list.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        dragging = true;
        moved = false;
        startX = event.clientX;
        startLeft = list.scrollLeft;
      });
      window.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        const dx = event.clientX - startX;
        if (!moved && Math.abs(dx) > 5) {
          moved = true;
          list.classList.add('is-dragging');
        }
        if (moved) list.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!dragging) return;
        dragging = false;
        if (moved) setTimeout(() => list.classList.remove('is-dragging'), 0);
      });
      list.addEventListener('dragstart', (event) => event.preventDefault());
    }
  }

  if (!customElements.get('esp-brand-page')) customElements.define('esp-brand-page', EspBrandPage);
})();
